import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, Download, Loader2, Scissors, Smartphone, Trash2, X, XCircle } from 'lucide-react';
import { Role } from '@jeket-cloud/types/auth';
import { RoleGate } from '@/components/auth/RoleGate';
import { socialApi } from '@/api/client';
import { cn } from '@/lib/utils';
import { useClipReview, useClips, useCreateJob, useProcessingSettings, useRecordings } from '@/hooks/useProcessing';
import type { ApproveClipResponse, Clip, Recording } from '@/types/processing';
import type { SocialAccount } from '@/types';
import {
  EmptyState,
  ErrorState,
  InlineError,
  LoadingState,
  PageHeader,
  StatusBadge,
  TRIGGER_LABEL,
  buttonPrimary,
  buttonSecondary,
  formatDuration,
  formatWallclock,
  iconButton,
  inputClass,
} from './shared';

type ReviewFilter = 'review' | 'approved' | 'rejected' | 'all';
const FILTERS: { id: ReviewFilter; label: string }[] = [
  { id: 'review', label: 'To review' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' },
];

function matches(clip: Clip, filter: ReviewFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'approved') return clip.approved;
  if (filter === 'rejected') return !!clip.rejected;
  return !clip.approved && !clip.rejected;
}

const DEFAULT_ASPECTS = ['9:16', '1:1'];

function ApproveDialog({ clip, onClose }: { clip: Clip; onClose: () => void }) {
  const { approve } = useClipReview();
  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['social-accounts'],
    queryFn: () => socialApi.listAccounts(),
  });
  const active = accounts.filter((a: SocialAccount) => a.status === 'active');
  const [selected, setSelected] = useState<string[]>([]);
  const [outcome, setOutcome] = useState<ApproveClipResponse | null>(null);
  const renditions = clip.social.filter((s) => s.url);
  const [aspect, setAspect] = useState<string>(renditions[0]?.aspect ?? '16:9');
  const [caption, setCaption] = useState(clip.title ?? '');

  const submit = () => {
    const chosen = active.filter((a) => selected.includes(a.id));
    approve.mutate(
      {
        jobId: clip.jobId,
        data: {
          ...(chosen.length > 0
            ? { platforms: [...new Set(chosen.map((a) => a.platform))], accountIds: chosen.map((a) => a.id) }
            : {}),
          aspect,
          ...(caption.trim() ? { caption: caption.trim() } : {}),
        },
      },
      {
        // Stay open to report platforms that got no draft.
        onSuccess: (res) => (res.skippedPlatforms?.length ? setOutcome(res) : onClose()),
      },
    );
  };

  if (outcome) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-label="Approve clip">
        <div className="bg-jeket-black-elevated border border-jeket-gold/20 rounded-xl shadow-jeket w-full max-w-md p-6 space-y-3">
          <h2 className="text-lg font-semibold text-jeket-silver-bright">Clip approved</h2>
          {outcome.posts && outcome.posts.length > 0 && (
            <p className="text-sm text-jeket-healthy">
              Draft posts created for {[...new Set(outcome.posts.map((p) => p.platform))].join(', ')}. Publish them from{' '}
              <Link to="/media/social" className="underline">Social</Link>.
            </p>
          )}
          <ul className="text-sm text-jeket-warning space-y-1">
            {outcome.skippedPlatforms?.map((sp) => (
              <li key={sp.platform}>
                No draft for <span className="capitalize">{sp.platform}</span>: {sp.reason}
              </li>
            ))}
          </ul>
          <div className="flex justify-end">
            <button type="button" onClick={onClose} className={buttonPrimary}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-label="Approve clip">
      <div className="bg-jeket-black-elevated border border-jeket-gold/20 rounded-xl shadow-jeket w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-jeket-silver-bright">Approve clip</h2>
          <button type="button" onClick={onClose} className="text-jeket-silver-muted hover:text-jeket-silver-bright" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <span className="block text-sm text-jeket-silver-muted mb-2">Draft social posts for</span>
            {isLoading ? (
              <Loader2 size={16} className="animate-spin text-jeket-gold" />
            ) : active.length === 0 ? (
              <p className="text-xs text-jeket-silver-muted">
                No linked social accounts. Approving marks the clip approved without drafting posts;{' '}
                <Link to="/media/social" className="text-jeket-gold hover:underline">link an account</Link> to post.
              </p>
            ) : (
              <div className="space-y-1.5">
                {active.map((a) => {
                  const on = selected.includes(a.id);
                  return (
                    <label key={a.id} className={cn('flex items-center gap-3 p-2 rounded-lg border cursor-pointer', on ? 'border-jeket-gold/40 bg-jeket-gold/10' : 'border-jeket-gold/10')}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => setSelected(on ? selected.filter((x) => x !== a.id) : [...selected, a.id])}
                        className="accent-jeket-gold"
                      />
                      <span className="text-sm text-jeket-silver-bright capitalize">{a.platform}</span>
                      <span className="text-xs text-jeket-silver-muted truncate">{a.account_name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <label className="block">
            <span className="block text-sm text-jeket-silver-muted mb-1">Format</span>
            <select value={aspect} onChange={(e) => setAspect(e.target.value)} className={cn(inputClass, 'w-full')}>
              <option value="16:9">16:9 (original)</option>
              {renditions.map((r) => (
                <option key={r.aspect} value={r.aspect}>{r.aspect}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-sm text-jeket-silver-muted mb-1">Caption</span>
            <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} className={cn(inputClass, 'w-full')} />
          </label>
        </div>

        <InlineError error={approve.error} />
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-jeket-silver-muted hover:text-jeket-silver-bright rounded-lg">Cancel</button>
          <button type="button" onClick={submit} disabled={approve.isPending} className={buttonPrimary}>
            <Check size={14} />
            {approve.isPending ? 'Approving…' : selected.length > 0 ? `Approve & draft posts (${selected.length})` : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClipCard({ clip, recording, aspects }: { clip: Clip; recording?: Recording; aspects: string[] }) {
  const { reject, remove } = useClipReview();
  const createJob = useCreateJob();
  const [view, setView] = useState<string>('original');
  const [approving, setApproving] = useState(false);

  const rendition = clip.social.find((s) => s.aspect === view);
  const src = view === 'original' ? clip.url : rendition?.url ?? null;
  const vertical = view === '9:16' || view === '4:5';
  const ready = clip.status === 'completed' && !!clip.url;
  const clipStreamId = clip.streamId ?? recording?.streamId;
  const error = reject.error ?? remove.error ?? createJob.error;

  return (
    <article className="bg-jeket-black-surface border border-jeket-gold/20 rounded-xl overflow-hidden flex flex-col" data-testid={`clip-${clip.jobId}`}>
      <div className="bg-black flex items-center justify-center aspect-video">
        {src ? (
          <video key={src} src={src} controls preload="metadata" playsInline className={cn('max-h-full', vertical ? 'h-full' : 'w-full')} />
        ) : (
          <div className="flex flex-col items-center gap-2 text-jeket-silver-muted text-sm">
            {clip.status === 'failed' ? <XCircle size={24} className="text-jeket-error" /> : <Loader2 size={24} className="animate-spin text-jeket-gold" />}
            <StatusBadge status={rendition?.status ?? clip.status} />
          </div>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col gap-3">
        <div>
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-medium text-jeket-silver-bright">{clip.title ?? `Clip · ${formatDuration(clip.end - clip.start)}`}</h3>
            {clip.score != null && <span className="text-xs text-jeket-gold whitespace-nowrap">{Math.round(clip.score * 100)}%</span>}
          </div>
          {clip.reason && <p className="text-xs text-jeket-silver-muted mt-1">{clip.reason}</p>}
          <p className="text-[11px] text-jeket-silver-muted mt-1">
            {recording ? (
              <Link to={`/media/recordings/${encodeURIComponent(recording.sourceId)}`} className="hover:text-jeket-gold">
                {recording.label || recording.streamId}
              </Link>
            ) : (clip.streamId ?? '')}
            {' · '}
            {formatWallclock(clip.start)} · {formatDuration(clip.end - clip.start)}
            {clip.trigger && ` · ${TRIGGER_LABEL[clip.trigger]}`}
          </p>
        </div>

        <div className="flex items-center gap-1 flex-wrap" role="group" aria-label="Format">
          {['original', ...clip.social.map((s) => s.aspect)].map((a) => (
            <button
              key={a}
              type="button"
              aria-pressed={view === a}
              onClick={() => setView(a)}
              className={cn(
                'px-2 py-1 text-xs rounded border',
                view === a ? 'border-jeket-gold/40 bg-jeket-gold/10 text-jeket-gold' : 'border-jeket-gold/10 text-jeket-silver-muted hover:text-jeket-silver-bright',
              )}
            >
              {a === 'original' ? '16:9' : a}
            </button>
          ))}
          {src && (
            <a href={src} target="_blank" rel="noreferrer" className={cn(iconButton, 'ml-auto hover:text-jeket-gold')} title="Download" aria-label="Download">
              <Download size={14} />
            </a>
          )}
        </div>

        <div className="mt-auto flex items-center gap-2 flex-wrap">
          {clip.approved && <span className="px-2 py-0.5 text-xs rounded bg-jeket-healthy/10 text-jeket-healthy">Approved</span>}
          {clip.rejected && <span className="px-2 py-0.5 text-xs rounded bg-jeket-error/10 text-jeket-error">Rejected</span>}
          <RoleGate role={Role.SiteOperator}>
            <div className="flex items-center gap-2 flex-wrap ml-auto">
              {ready && clipStreamId && clip.social.length === 0 && (
                <button
                  type="button"
                  className={buttonSecondary}
                  disabled={createJob.isPending}
                  onClick={() =>
                    createJob.mutate({
                      feature: 'social_format',
                      streamId: clipStreamId,
                      start: clip.start,
                      end: clip.end,
                      params: { clipJobId: clip.jobId, clipFlowId: clip.flowId, aspects },
                    })
                  }
                >
                  <Smartphone size={12} /> Social formats
                </button>
              )}
              {!clip.approved && (
                <button type="button" onClick={() => setApproving(true)} disabled={!ready} className={cn(buttonSecondary, 'text-jeket-healthy border-jeket-healthy/30 bg-jeket-healthy/10 hover:bg-jeket-healthy/20')}>
                  <Check size={12} /> Approve
                </button>
              )}
              {!clip.rejected && !clip.approved && (
                <button type="button" onClick={() => reject.mutate(clip.jobId)} disabled={reject.isPending} className={cn(buttonSecondary, 'text-jeket-error border-jeket-error/30 bg-jeket-error/10 hover:bg-jeket-error/20')}>
                  <X size={12} /> Reject
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (confirm('Delete this clip? Its media is removed from the store.')) remove.mutate(clip.jobId);
                }}
                disabled={remove.isPending}
                className={cn(iconButton, 'hover:text-jeket-error')}
                title="Delete clip"
                aria-label="Delete clip"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </RoleGate>
        </div>
        <InlineError error={error} />
      </div>

      {approving && <ApproveDialog clip={clip} onClose={() => setApproving(false)} />}
    </article>
  );
}

export default function ClipsReviewPage() {
  const [filter, setFilter] = useState<ReviewFilter>('review');
  const [streamId, setStreamId] = useState('');
  const { data: clips = [], isLoading, isError, error } = useClips(streamId || undefined);
  const { data: recordings = [] } = useRecordings();
  const { data: settings } = useProcessingSettings();
  const byStream = useMemo(() => new Map(recordings.map((r) => [r.streamId, r])), [recordings]);

  const counts = Object.fromEntries(FILTERS.map((f) => [f.id, clips.filter((c) => matches(c, f.id)).length]));
  const shown = clips.filter((c) => matches(c, filter)).sort((a, b) => b.start - a.start);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Clip review"
        subtitle="Approve clips for social distribution, or reject them."
        actions={
          <select aria-label="Filter by recording" value={streamId} onChange={(e) => setStreamId(e.target.value)} className={inputClass}>
            <option value="">All recordings</option>
            {recordings.map((r) => (
              <option key={r.sourceId} value={r.streamId}>{r.label || r.streamId}</option>
            ))}
          </select>
        }
      />

      <div className="flex items-center gap-1 flex-wrap mb-4" role="group" aria-label="Review state">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
            className={cn(
              'px-3 py-1.5 text-sm rounded-lg transition-colors border',
              filter === f.id
                ? 'bg-jeket-gold/20 text-jeket-gold border-jeket-gold/30'
                : 'text-jeket-silver-muted hover:text-jeket-silver-bright hover:bg-jeket-black-elevated border-transparent',
            )}
          >
            {f.label}
            {counts[f.id] > 0 && <span className="ml-1.5 text-xs opacity-70">({counts[f.id]})</span>}
          </button>
        ))}
      </div>

      {isLoading ? (
        <LoadingState label="Loading clips…" />
      ) : isError ? (
        <ErrorState title="Couldn't load clips" error={error} />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={Scissors}
          title={clips.length === 0 ? 'No clips yet' : 'Nothing here'}
          hint="Clips are cut from highlight marks during an event, or from an in/out selection on a recording's timeline."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {shown.map((c) => (
            <ClipCard key={c.jobId} clip={c} recording={byStream.get(c.streamId ?? streamId)} aspects={settings?.socialAspects ?? DEFAULT_ASPECTS} />
          ))}
        </div>
      )}
    </div>
  );
}
